import React, { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Search, X } from "lucide-react";
import { customersAPI } from "@/lib/api";
import { cn } from "@/lib/utils";

interface PickerCustomer {
	id: number;
	firstName?: string;
	lastName?: string;
	companyName?: string;
	phone?: string;
	email?: string;
}

export const customerDisplayName = (c: PickerCustomer) =>
	[c.firstName, c.lastName].filter(Boolean).join(" ") || c.companyName || `#${c.id}`;

const RESULT_LIMIT = 20;
const SEARCH_DELAY_MS = 250;

export interface CustomerPickerProps {
	/** Selected customer id, or "" for none. */
	value: string;
	onChange: (customerId: string) => void;
	placeholder: string;
	/** Shows a clear button so the field can be left empty. */
	clearable?: boolean;
	id?: string;
	"aria-invalid"?: boolean;
}

/**
 * Searchable customer field. Searches on the server (name, phone, email),
 * so it works the same with 10 customers or 10,000.
 */
export const CustomerPicker: React.FC<CustomerPickerProps> = ({
	value,
	onChange,
	placeholder,
	clearable = false,
	id,
	"aria-invalid": ariaInvalid,
}) => {
	const { t } = useTranslation();
	const listId = useId();
	const containerRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");
	const [results, setResults] = useState<PickerCustomer[]>([]);
	const [loading, setLoading] = useState(false);
	const [failed, setFailed] = useState(false);
	const [highlighted, setHighlighted] = useState(0);
	const [selected, setSelected] = useState<PickerCustomer | null>(null);

	// Resolve the label of a value set from outside (e.g. editing a saved quotation).
	useEffect(() => {
		if (!value) {
			setSelected(null);
			return;
		}
		if (selected && String(selected.id) === value) return;
		let cancelled = false;
		customersAPI
			.getById(value)
			.then((c: PickerCustomer) => !cancelled && setSelected(c))
			.catch(() => !cancelled && setSelected({ id: Number(value) }));
		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps -- only re-resolve when the value changes
	}, [value]);

	// Debounced server search while the list is open.
	useEffect(() => {
		if (!open) return;
		const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
		let cancelled = false;
		setLoading(true);
		setFailed(false);
		const timer = window.setTimeout(() => {
			// The server matches one field at a time, so send the first word and narrow by the rest here.
			customersAPI
				.getAll({ page: 1, limit: RESULT_LIMIT, search: words[0] || undefined })
				.then((data: { customers?: PickerCustomer[] }) => {
					if (cancelled) return;
					const list = (data?.customers ?? []).filter(c => {
						const haystack = [customerDisplayName(c), c.phone, c.email].join(" ").toLowerCase();
						return words.every(w => haystack.includes(w));
					});
					setResults(list);
					setHighlighted(0);
				})
				.catch(() => {
					if (cancelled) return;
					setResults([]);
					setFailed(true);
				})
				.finally(() => !cancelled && setLoading(false));
		}, SEARCH_DELAY_MS);
		return () => {
			cancelled = true;
			window.clearTimeout(timer);
		};
	}, [open, query]);

	// Close when clicking outside.
	useEffect(() => {
		if (!open) return;
		const onPointerDown = (e: MouseEvent) => {
			if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
		};
		document.addEventListener("mousedown", onPointerDown);
		return () => document.removeEventListener("mousedown", onPointerDown);
	}, [open]);

	const openList = () => {
		setQuery("");
		setOpen(true);
	};

	const choose = (c: PickerCustomer) => {
		setSelected(c);
		onChange(String(c.id));
		setOpen(false);
		setQuery("");
	};

	const clear = () => {
		setSelected(null);
		onChange("");
		setQuery("");
	};

	const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
			e.preventDefault();
			openList();
			return;
		}
		if (!open) return;
		if (e.key === "ArrowDown") {
			e.preventDefault();
			setHighlighted(i => Math.min(i + 1, results.length - 1));
		} else if (e.key === "ArrowUp") {
			e.preventDefault();
			setHighlighted(i => Math.max(i - 1, 0));
		} else if (e.key === "Enter") {
			e.preventDefault();
			if (results[highlighted]) choose(results[highlighted]);
		} else if (e.key === "Escape") {
			e.preventDefault();
			setOpen(false);
		} else if (e.key === "Tab") {
			setOpen(false);
		}
	};

	const optionId = (index: number) => `${listId}-option-${index}`;

	return (
		<div ref={containerRef} className="relative">
			<Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
			<input
				ref={inputRef}
				id={id}
				type="text"
				role="combobox"
				aria-expanded={open}
				aria-controls={listId}
				aria-autocomplete="list"
				aria-invalid={ariaInvalid}
				aria-activedescendant={open && results[highlighted] ? optionId(highlighted) : undefined}
				autoComplete="off"
				value={open ? query : selected ? customerDisplayName(selected) : ""}
				placeholder={open ? t("customers.picker.searchPlaceholder") : placeholder}
				onFocus={() => !open && openList()}
				onClick={() => !open && openList()}
				onChange={e => {
					setQuery(e.target.value);
					if (!open) setOpen(true);
				}}
				onKeyDown={onKeyDown}
				className={cn(
					"flex h-12 w-full rounded-md border border-input bg-background ps-9 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
					clearable && value && !open ? "pe-14" : "pe-8",
					ariaInvalid && "border-destructive",
				)}
			/>
			<div className="absolute end-2 top-1/2 flex -translate-y-1/2 items-center gap-1">
				{clearable && value && !open && (
					<button
						type="button"
						onClick={clear}
						className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
						title={t("common.clear")}
						aria-label={t("common.clear")}
					>
						<X className="h-4 w-4" />
					</button>
				)}
				<ChevronDown className="pointer-events-none h-4 w-4 opacity-50" />
			</div>

			{open && (
				<div
					id={listId}
					role="listbox"
					aria-busy={loading}
					className={cn(
						"absolute z-50 mt-1 max-h-64 w-full overflow-y-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md transition-opacity",
						loading && results.length > 0 && "opacity-60",
					)}
				>
					{loading && results.length === 0 ? (
						<div className="px-3 py-2 text-sm text-muted-foreground">{t("common.loading")}</div>
					) : failed ? (
						<div className="px-3 py-2 text-sm text-destructive">{t("customers.picker.loadFailed")}</div>
					) : results.length === 0 ? (
						<div className="px-3 py-2 text-sm text-muted-foreground">{t("customers.picker.noResults")}</div>
					) : (
						results.map((c, index) => (
							<div
								key={c.id}
								id={optionId(index)}
								role="option"
								aria-selected={String(c.id) === value}
								onMouseDown={e => e.preventDefault()}
								onClick={() => choose(c)}
								onMouseEnter={() => setHighlighted(index)}
								className={cn(
									"flex cursor-pointer flex-col rounded-sm px-3 py-2 text-sm",
									index === highlighted && "bg-accent text-accent-foreground",
									String(c.id) === value && "font-semibold",
								)}
							>
								<span className="truncate">{customerDisplayName(c)}</span>
								{(c.phone || c.email) && (
									<span
										dir="ltr"
										className="max-w-full self-start truncate text-xs text-muted-foreground"
									>
										{c.phone || c.email}
									</span>
								)}
							</div>
						))
					)}
				</div>
			)}
		</div>
	);
};
